/**
 * Manages "Send From" integration selection. Handles loading available integrations,
 * auto-selecting based on recipient format, and smart field clearing when switching channels.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { UseFormReturn, useWatch } from 'react-hook-form';

import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { Integration, IntegrationStatus, IntegrationType } from '@/shared/types/integration';
import {
  getRecipientType,
  DIRECT_MESSAGE_INTEGRATION_TYPES,
  RecipientType,
} from '@/util/outboundIntegrations';
import { emailPattern } from '@/util/validation/patterns';

import { NewMessageFormValues } from './useNewMessageForm';

interface UseNewMessageIntegrationsProps {
  form: UseFormReturn<NewMessageFormValues>;
  initialTo?: string;
}

/** Detects if recipient looks like email or phone for auto-selecting a compatible integration. */
const detectRecipientType = (recipientValue: string): RecipientType | null => {
  if (!recipientValue) {
    return null;
  }
  if (emailPattern.test(recipientValue)) {
    return 'email';
  }

  // 7+ digits = probably a phone number
  const digitsOnly = recipientValue.replace(/\D/g, '');
  if (digitsOnly.length >= 7) {
    return 'phone';
  }

  return null;
};

/** Manages integration selection, auto-selection based on recipient, and field clearing on switch. */
export const useNewMessageIntegrations = ({ form, initialTo }: UseNewMessageIntegrationsProps) => {
  const deskId = useDeskId();
  const { control, setValue, clearErrors } = form;
  const selectedIntegrationId = useWatch({ control, name: 'integrationId' });

  // Only auto-select once per modal open. Reset when initialTo changes (modal reopened).
  const hasAutoSelected = useRef(false);
  const prevInitialToRef = useRef(initialTo);

  const { integrations: allIntegrations, listStatus: integrationsStatus } = useIntegrations(
    deskId || ''
  );

  const integrations = useMemo(() => {
    if (!allIntegrations) {
      return [];
    }

    return allIntegrations.filter(
      (integration) =>
        DIRECT_MESSAGE_INTEGRATION_TYPES.includes(integration.type as IntegrationType) &&
        integration.active &&
        integration.status === IntegrationStatus.VERIFIED
    );
  }, [allIntegrations]);

  const hasNoIntegrations = integrationsStatus !== 'pending' && integrations.length === 0;

  const selectedIntegration = useMemo((): Integration | null => {
    if (!selectedIntegrationId || !allIntegrations) {
      return null;
    }
    return (
      (allIntegrations.find((i) => i.integration_id === selectedIntegrationId) as Integration) ??
      null
    );
  }, [selectedIntegrationId, allIntegrations]);

  const selectedIntegrationType = (selectedIntegration?.type as IntegrationType) || null;

  // Sync integrationType when integrationId was pre-filled (e.g. from follow-up click).
  useEffect(() => {
    if (hasAutoSelected.current || !selectedIntegrationId || !integrations?.length) {
      return;
    }

    const preSelected = integrations.find((i) => i.integration_id === selectedIntegrationId);

    if (preSelected) {
      setValue('integrationType', preSelected.type as IntegrationType, { shouldValidate: true });
      hasAutoSelected.current = true;
    }
  }, [selectedIntegrationId, integrations, setValue]);

  // Auto-select the first compatible integration based on pre-filled recipient format.
  useEffect(() => {
    if (prevInitialToRef.current !== initialTo) {
      hasAutoSelected.current = false;
      prevInitialToRef.current = initialTo;
    }

    if (hasAutoSelected.current || !initialTo || !integrations?.length) {
      return;
    }

    const recipientType = detectRecipientType(initialTo);
    if (!recipientType) {
      return;
    }

    const compatibleIntegration = integrations.find(
      (i) => getRecipientType(i.type as IntegrationType) === recipientType
    );

    if (compatibleIntegration) {
      setValue('integrationId', compatibleIntegration.integration_id || '');
      setValue('integrationType', compatibleIntegration.type as IntegrationType, {
        shouldValidate: true,
      });
    }
    hasAutoSelected.current = true;
  }, [initialTo, integrations, setValue]);

  /** Handles switching integrations. Clears recipient when switching between phone and email channels. */
  const onIntegrationChange = useCallback(
    (newIntegrationId: string) => {
      if (newIntegrationId === selectedIntegrationId) {
        setValue('integrationId', '', { shouldDirty: true, shouldValidate: true });
        setValue('integrationType', null, { shouldValidate: true });
        setValue('templateId', null);
        setValue('headerParameters', []);
        setValue('bodyParameters', []);
        setValue('footerParameters', []);
        setValue('headerMediaParam', null);
        setValue('content', '');
        setValue('subject', '');
        clearErrors();
        return;
      }

      const newIntegration = allIntegrations?.find((i) => i.integration_id === newIntegrationId);
      const newIntegrationType = newIntegration?.type as IntegrationType | null;

      const previousRecipientType = getRecipientType(selectedIntegrationType as IntegrationType);
      const newRecipientType = getRecipientType(newIntegrationType as IntegrationType);

      setValue('integrationId', newIntegrationId, { shouldDirty: true, shouldValidate: true });
      setValue('integrationType', newIntegrationType, { shouldValidate: true });

      if (previousRecipientType !== null && previousRecipientType !== newRecipientType) {
        setValue('toPhone', '');
        setValue('toEmail', '');
      }

      if (selectedIntegrationType !== newIntegrationType) {
        setValue('templateId', null);
        setValue('headerParameters', []);
        setValue('bodyParameters', []);
        setValue('footerParameters', []);
        setValue('headerMediaParam', null);
        setValue('content', '');
        setValue('subject', '');
      }

      clearErrors();
    },
    [setValue, clearErrors, selectedIntegrationType, selectedIntegrationId, allIntegrations]
  );

  return {
    integrations,
    integrationsLoading: integrationsStatus === 'pending',
    selectedIntegration,
    selectedIntegrationType,
    hasNoIntegrations,
    onIntegrationChange,
  };
};
